const { experimentSources, switchboardIdTypes, DEFAULT_COHORT } = require('./constants');

const reportedIdTypes = new Set([
  switchboardIdTypes.VISITOR_ID,
  switchboardIdTypes.CUSTOMER_ID,
  switchboardIdTypes.SHOPPER_ID
]);

const PROBABLY_BASELINE = /\b(?:baseline|control|default|off)/i;

function settingsToExperiments(settings) {
  return Object.fromEntries(
    settings.map(({ setting, rules, metadata }) => {
      const cohortData = rulesetToCohortData(rules);
      const cohorts = sortedCohortIds(cohortData, metadata || {});
      const baselineCohort = cohorts[0];
      return [
        setting,
        {
          baselineCohort,
          experimentId: setting,
          source: experimentSources.SWITCHBOARD,
          subjectIdType: rulesetToSubjectIdType(rules),
          killed: false,
          labels: (metadata && metadata.labels) || [],
          cohorts,
          cohortData
        }
      ];
    })
  );
}

function rulesetToCohortData({ value, values }) {
  if (!values) {
    return rulesetToCohortData({ values: [{ value, isDefault: true }] });
  }

  return Object.assign({}, ...values.map(({ value: { cohortId, data } }) => ({
    [cohortId]: data
  })));
}

function rulesetToSubjectIdType({ values = [] }) {
  for (const { rule } of values) {
    const idType = rule && ruleToSubjectIdType(rule);
    if (idType) {
      return idType;
    }
  }

  return null;
}

function ruleToSubjectIdType(rule) {
  let idType = null;

  // eslint-disable-next-line default-case
  switch (rule.type) {
    case 'idPercentage':
    case 'idList':
      idType = rule.idType;
      break;
    case 'shopperPercentage':
    case 'shopperList':
      idType = switchboardIdTypes.SHOPPER_ID;
      break;
    case 'not':
      idType = ruleToSubjectIdType(rule.rule);
      break;
    case 'and':
    case 'or':
      for (const operand of rule.rules) {
        idType = ruleToSubjectIdType(operand);
        if (idType) {
          break;
        }
      }
  }

  return reportedIdTypes.has(idType) ? idType : null;
}

const A_COMES_BEFORE_B = -1;
const B_COMES_BEFORE_A = 1;

function sortedCohortIds(cohortData, { baselineCohort }) {
  // At least one consumer of hivemind experiments relies on cohorts IDs being
  // sorted with the control variant coming before the treatments. The control
  // cohort ID will be in the setting metadata in the future, but for older
  // settings this may be missing, so we'll use typical naming conventions
  // to guess at the control side of the experiment:
  //
  // * When using terms like "on" and "off", "off" will typically be control.
  // * When using terms like "control" and "treatment", "control" will be control.
  // * When using "a" and "b", "a" will be control. To implement this, we'll
  //   just use alphabetical sorting.
  //
  // `ineligible` will always go last.

  return Object.keys(cohortData).sort((a, b) => {
    if (a === DEFAULT_COHORT) {
      return B_COMES_BEFORE_A;
    }

    if (b === DEFAULT_COHORT) {
      return A_COMES_BEFORE_B;
    }

    if (a === baselineCohort || (!baselineCohort && PROBABLY_BASELINE.test(a))) {
      return A_COMES_BEFORE_B;
    }

    if (b === baselineCohort || (!baselineCohort && PROBABLY_BASELINE.test(b))) {
      return B_COMES_BEFORE_A;
    }

    return a.localeCompare(b);
  });
}

module.exports = {
  settingsToExperiments
};
